import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

/**
 * Full Terms & Conditions page – copy‑paste into /pages/terms.js
 * Uses the project fonts: “Carattere” for the hero heading,
 * “Josefin Sans” everywhere else.  No external CSS file required.
 */
export default function Terms() {
  const navigate = useNavigate();
  return (
    <div className="terms-page">
      {/* ——— inline CSS ——— */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Carattere&family=Josefin+Sans:wght@300;400;500;600&display=swap');

        .terms-page{
          font-family:'Josefin Sans',sans-serif;
          padding:4rem 9vw 6rem;
          max-width:900px;
          margin:0 auto;
          line-height:1.55;
          color:#222;
          height:100vh;
          overflow:scroll;
        }
        .terms-page h1{
          font-family:'Carattere',cursive;
          font-size:3.4rem;
          color:#b3002d;
          margin:0 0 0.6rem;
        }
        .terms-page h2{
          font-size:1.6rem;
          color:#b3002d;
          margin:2.8rem 0 0.8rem;
        }
        .terms-page h3{
          font-size:1.25rem;
          color:#b3002d;
          margin:2rem 0 0.6rem;
        }
        .terms-page ul{margin-left:1.2rem;}
        .terms-page a{color:#b3002d;text-decoration:underline;}
        .back-home{
          position:fixed;
          top:1rem;left:1rem;
          display:flex;align-items:center;justify-content:center;
          width:44px;height:44px;border-radius:9px;
          background:#ffffffcc;color:#b3002d;
          text-decoration:none;font-size:0;
          box-shadow:0 1px 4px rgba(0,0,0,.15);
        }
      `}</style>

      {/* back‑home button (home icon – simple SVG) */}
      {/* <button
       className="back-home"
       aria-label="Back"
       onClick={() => navigate(-1)}                  // ← go to previous page
     >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 12L2 13l10 9 10-9-1-1M5 10V5a1 1 0 0 1 1-1h3" />
          <path d="M19 10V5a1 1 0 0 0-1-1h-3" />
          <path d="M12 22V12" />
        </svg>
      </button> */}

      {/* ——— document ——— */}
      <h1>Terms &amp; Conditions</h1>
      <p><em>Last updated 21 April 2025</em></p>

      <h2>1. Definitions</h2>
      <p><strong>“Bespoke Goods”</strong> – any item designed or altered to your specification (including CAD models, renders and finished jewellery).<br/>
         <strong>“Travel Ring”</strong> – complimentary replica ring supplied under the Offer (see § 9).<br/>
         <strong>“Offer”</strong> – promotional price of <strong>£1 499</strong> for a ring of up to 2 ct VVS diamonds in 14 ct gold.</p>

      <h2>2. Scope of Services</h2>
      <ul>
        <li>Free design consultation(s) via video call, phone or in person.</li>
        <li>Preparation of CAD drawings &amp; photo‑realistic renders by third‑party specialists.</li>
        <li>Manufacture of Bespoke Goods by vetted third‑party workshops.</li>
        <li>Optional 0 % finance arranged through a third‑party, FCA‑regulated lender (see § 7).</li>
        <li>Delivery or personal collection, hallmarked and supplied with IGI (or equivalent) certificates.</li>
      </ul>

      <h2>3. Design Material & Intellectual Property</h2>
      <p>3.1 You may share images (e.g. Pinterest boards) as inspiration. You confirm you have the right to use any material you supply and you <em>indemnify us</em> against third‑party IP claims.<br/>
         3.2 All CAD files, renders and technical drawings remain our copyright until full payment is received. We grant you a non‑exclusive licence for personal use only.<br/>
         3.3 We may photograph completed pieces for our portfolio unless you object in writing.</p>

      <h2>4. Quotations & Price Adjustments</h2>
      <ul>
        <li>Prices are quoted in GBP inclusive of UK VAT.</li>
        <li>A quotation is valid for 14 days; after that we may re‑quote due to metal/diamond fluctuations.</li>
        <li>Diamond carat weights may vary ±0.05 ct; gold purity is hallmarked to the stated fineness.</li>
      </ul>

      <h2>5. Deposits & Payments</h2>
      <ul>
        <li><strong>20 % non‑refundable deposit</strong> is due once you approve the final CAD design.</li>
        <li>Balance payable&nbsp;– via approved finance (§ 7) or in full on/before collection or dispatch.</li>
        <li>After notification that the ring is ready, the balance must be paid within 10 calendar days.  
            If unpaid, the order is cancelled, deposit forfeited and we may dispose of the ring.</li>
        <li>Accepted methods: bank transfer, UK debit/credit card, finance‑provider portal.</li>
      </ul>

      <h2>6. Manufacture & Lead‑Times</h2>
      <p>Standard lead‑time is 4 weeks from cleared deposit. Express options available at extra cost.</p>

      <h2>7. Finance</h2>
      <p>Where offered, credit agreements are provided by an <em>independent, FCA‑regulated lender</em> (“Finance Provider”). We act only as a credit broker and will confirm the Finance Provider’s identity in writing before you apply.<br/>
         Availability, APR and approval are solely at the Finance Provider’s discretion.  
         If finance is declined, § 5 applies and you must settle the balance directly with us.</p>

      <h2>8. Collection, Delivery & Risk</h2>
      <ul>
        <li>Collection: free appointment in London (£30 optional fitting).</li>
        <li>Delivery: insured next‑day UK shipping after live video inspection &amp; full payment.</li>
        <li>Risk passes on collection or delivery to the carrier; title passes on full payment.</li>
      </ul>

      <h2>9. Complimentary Travel Ring</h2>
      <p>The Travel Ring is an <strong>aesthetic replica</strong> of your main ring, crafted from 925 silver and laboratory‑grown moissanite so it can be worn where risk of loss or damage is higher.<br/>
         Upgrades (e.g. gold plating) are available by quotation.  
         It has no cash value and is non‑returnable.</p>

      <h2>10. Warranty & After‑Care</h2>
      <p>We warrant Bespoke Goods against manufacturing defects for <strong>12 months</strong> from delivery.  
         This excludes normal wear, accidental damage, misuse and unauthorised alterations.<br/>
         Repairs, resizing or rhodium‑plating after the warranty period can be arranged at current rates.</p>

      <h2>11. Returns & Cancellations</h2>
      <p>Bespoke Goods are exempt from the 14‑day “cooling‑off” period. Deposits are non‑refundable. Your statutory rights regarding faulty goods remain intact.</p>

      <h2>12. Limitation of Liability</h2>
      <p>Our total liability shall not exceed the price paid for the Bespoke Goods; we are not liable for indirect or consequential loss. Nothing limits liability for death or personal injury caused by negligence or matters that cannot be excluded under law.</p>

      <h2>13. Force Majeure</h2>
      <p>We are not responsible for delays caused by events beyond our reasonable control (e.g. natural disasters, transport strikes).</p>

      <h2>14. Data Protection</h2>
      <p>Your data are processed per the UK GDPR and our Privacy Notice (<a href="/privacy">customcarats.com/privacy</a>).</p>

      <h2>15. Governing Law</h2>
      <p>These Terms are governed by English law; disputes are subject to the exclusive jurisdiction of the English courts.</p>

      <h2>16. Contact</h2>
      <p><strong>Custom Carats</strong><br/>
         E‑mail: <a href="mailto:admin@customcarats.com">admin@customcarats.com</a> • Tel: [+44 7397906041]</p>
    </div>
  );
}